import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { PageLayout } from '../components/ui/Layout';
import { Card } from '../components/ui/Card';
import { PrimaryButton } from '../components/ui/Button';
import { calculateRelationshipScore, doGenderNameReplacements } from '../utils/scoreCalculator';
import { DEFAULT_QUIZ } from '../data/defaultQuiz';
import clsx from 'clsx';

const { FiCheck, FiChevronRight, FiAlertCircle, FiInfo } = FiIcons;

export default function QuizRunner() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [activeQuestions, setActiveQuestions] = useState([]);
  const [sessionMetadata, setSessionMetadata] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  
  const [step, setStep] = useState('quiz'); 
  const [userDiagnosis, setUserDiagnosis] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('ioweu_session');
    if (!stored) {
      navigate('/');
      return;
    }
    const metadata = JSON.parse(stored);
    setSessionMetadata(metadata);

    setActiveQuestions(DEFAULT_QUIZ.questions);
    
    // Initialize all potential answers
    const initialAnswers = {};
    [...DEFAULT_QUIZ.questions, ...DEFAULT_QUIZ.branches.a, ...DEFAULT_QUIZ.branches.b].forEach(q => {
      initialAnswers[q.id] = null;
    });
    setAnswers(initialAnswers);
  }, [navigate]);

  // Handle Branching Logic based on Q9 (You=Left/-2 vs Name=Right/2)
  useEffect(() => {
    // Check if Q9 has been answered
    if (answers['9'] !== undefined && answers['9'] !== null) {
      // If answer is negative (Left/You), use branch A
      const isYouCalled = answers['9'] < 0; 
      const branch = isYouCalled ? DEFAULT_QUIZ.branches.a : DEFAULT_QUIZ.branches.b;
      
      const baseQuestions = DEFAULT_QUIZ.questions;
      setActiveQuestions([...baseQuestions, ...branch]);
    } else {
      setActiveQuestions(DEFAULT_QUIZ.questions);
    }
  }, [answers['9']]);

  const handleAnswer = (qid, value) => {
    setAnswers(prev => ({ ...prev, [qid]: parseInt(value) }));
    setActiveQuestionId(qid);
  };

  const handleValidation = () => {
    if (!sessionMetadata) return false;
    
    // Filter active questions to check if they are answered
    const missedQuestions = activeQuestions.filter(q => answers[q.id] === null || answers[q.id] === undefined);
    
    if (missedQuestions.length > 0) {
        setShowErrors(true);
        const firstMissingId = missedQuestions[0].id;
        const element = document.getElementById(`question-${firstMissingId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => {
            alert(`Please complete all questions before submitting. You have missed ${missedQuestions.length} question(s).`);
        }, 100);
        return false;
    }
    return true;
  };

  const proceedToDiagnosis = () => {
    if (handleValidation()) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setStep('diagnosis');
    }
  };

  const saveToHistory = (result) => {
    try {
      const historyItem = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        meetingDate: new Date().toISOString(),
        meetingIndex: result.meetingIndex,
        // Map properties correctly from calculateRelationshipScore result
        normalizedScore: result.score?.pScore,
        relationshipType: result.score?.bestType,
        bestTypeLabel: result.bestTypeLabel,
        scoreString: result.scoreString,
        buyerName: sessionMetadata.buyerName,
        score: result.score
      };

      const existingHistory = JSON.parse(localStorage.getItem('quizzes_history') || '[]');
      existingHistory.push(historyItem);
      localStorage.setItem('quizzes_history', JSON.stringify(existingHistory));
    } catch (e) {
      console.error("Failed to save history", e);
    }
  };

  const submitFinalAssessment = () => {
    if (!userDiagnosis) {
        alert("Please select the relationship type you think best describes this interaction.");
        return;
    }

    const results = calculateRelationshipScore(
      answers, 
      DEFAULT_QUIZ, 
      sessionMetadata.meetingIndex,
      sessionMetadata.buyerName,
      sessionMetadata.buyerGender || 'male'
    );
    
    results.userDiagnosis = userDiagnosis;
    
    // Attach detailed data for Results/Export usage
    results.answers = answers;
    results.buyerName = sessionMetadata.buyerName;
    results.buyerGender = sessionMetadata.buyerGender || 'male';
    results.timestamp = new Date().toISOString();
    
    // Save to local storage history
    saveToHistory(results);

    navigate('/results', { state: { results } });
  };

  const replace = (text) => doGenderNameReplacements(
      text, 
      sessionMetadata?.userName, 
      sessionMetadata?.buyerName, 
      sessionMetadata?.buyerGender || 'male'
  );

  if (!sessionMetadata || activeQuestions.length === 0) return null;

  if (step === 'diagnosis') {
      const typeOptions = DEFAULT_QUIZ.typeDefinitions || [];

      return (
        <PageLayout>
             <div className="max-w-4xl mx-auto py-6 md:py-12">
                <Card className="p-5 md:p-8 space-y-6 md:space-y-8">
                    <div className="text-center space-y-3">
                        <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Self-Assessment</h2>
                        <p className="text-base md:text-lg text-slate-600">
                             {replace("Before we reveal your result, which of the following relationship types do you believe your style of interaction with [Name] is likely to result in?")}
                        </p>
                    </div>

                    <div className="space-y-3 md:space-y-4">
                        {typeOptions.map((option) => (
                            <button
                                key={option.id}
                                onClick={() => setUserDiagnosis(option.id)}
                                className={clsx(
                                    "w-full text-left p-4 md:p-6 rounded-xl border-2 transition-all duration-200 group relative overflow-hidden",
                                    userDiagnosis === option.id 
                                        ? "border-[#0270a9] bg-blue-50 shadow-md" 
                                        : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
                                )}
                            >
                                <div className="flex items-start gap-3 md:gap-4 relative z-10">
                                    <div className={clsx(
                                        "w-5 h-5 md:w-6 md:h-6 rounded-full border-2 mt-1 flex items-center justify-center flex-shrink-0 transition-colors",
                                        userDiagnosis === option.id ? "border-[#0270a9]" : "border-slate-300"
                                    )}>
                                        {userDiagnosis === option.id && (
                                            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#0270a9]" />
                                        )}
                                    </div>
                                    <div className="w-full">
                                        <h3 className={clsx(
                                            "text-lg md:text-xl font-bold mb-1 md:mb-2 transition-colors",
                                            userDiagnosis === option.id ? "text-[#0270a9]" : "text-slate-800"
                                        )}>
                                            {option.label}
                                        </h3>
                                        <div className="text-slate-600 leading-relaxed whitespace-pre-line text-xs md:text-base">
                                            {replace(option.desc)}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>

                    <PrimaryButton 
                        onClick={submitFinalAssessment} 
                        className="w-full py-3 md:py-4 text-lg md:text-xl"
                        disabled={!userDiagnosis}
                    >
                        View Results
                    </PrimaryButton>
                </Card>
             </div>
        </PageLayout>
      );
  }

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto pb-10 md:pb-20 space-y-4 md:space-y-8">
        <div className="text-center mb-4 md:mb-8">
             <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Assessment Questions</h1>
             <p className="text-slate-500 mt-1 md:mt-2 text-sm md:text-base">Complete all questions below.</p>
        </div>

        {activeQuestions.map((q) => (
           <QuestionCard 
             key={q.id} 
             question={q} 
             value={answers[q.id]} 
             onChange={(val) => handleAnswer(q.id, val)}
             metadata={sessionMetadata}
             isActive={activeQuestionId === q.id}
             hasError={showErrors && answers[q.id] === null}
             onActivate={() => setActiveQuestionId(q.id)}
             replaceFunc={replace}
           />
        ))}

        <div className="flex justify-center pt-4 md:pt-8">
            <PrimaryButton onClick={proceedToDiagnosis} className="px-8 md:px-12 py-3 md:py-4 text-lg md:text-xl">
               Next Step
            </PrimaryButton>
        </div>
      </div>
    </PageLayout>
  );
}

function QuestionCard({ question, value, onChange, metadata, isActive, hasError, onActivate, replaceFunc }) {
  const [showHelp, setShowHelp] = useState(false);

  const replace = replaceFunc || ((text) => doGenderNameReplacements(
      text, 
      metadata.userName, 
      metadata.buyerName, 
      metadata.buyerGender || 'male'
  ));

  const questionText = replace(question.questionText);
  const leftLabel = replace(question.leftLabel);
  const rightLabel = replace(question.rightLabel);
  const helpText = replace(question.helpText);

  return (
    <div 
        id={`question-${question.id}`}
        onClick={onActivate}
        className={clsx(
            "rounded-xl transition-all duration-300 border-2",
            isActive 
                ? "bg-slate-50 border-[#0270a9] shadow-lg ring-1 ring-[#0270a9]/10" 
                : "bg-white border-slate-200 shadow-sm",
            hasError && "!border-red-500 !ring-red-100 !bg-red-50/10"
        )}
    >
        <div className="p-4 md:p-8">
            <div className="flex justify-between items-start gap-4 mb-4 md:mb-6">
                <h3 className="text-lg md:text-xl font-bold text-slate-800 leading-snug">{questionText}</h3>
                <button 
                    onClick={(e) => { e.stopPropagation(); setShowHelp(!showHelp); }}
                    className={clsx(
                        "flex-shrink-0 p-2 rounded-full transition-colors",
                        showHelp ? "bg-[#0270a9] text-white" : "text-[#0270a9] bg-blue-50 hover:bg-blue-100"
                    )}
                    title="Toggle Help Text"
                >
                    <SafeIcon icon={FiInfo} className="text-lg md:text-xl" />
                </button>
            </div>
            
            <div className="mb-4 md:mb-6">
                {question.isBinary ? (
                    <div className="grid grid-cols-2 gap-3 md:gap-4">
                        <SelectionButton 
                            selected={value === -2} 
                            onClick={() => onChange(-2)} 
                            label={leftLabel} 
                        />
                        <SelectionButton 
                            selected={value === 2} 
                            onClick={() => onChange(2)} 
                            label={rightLabel} 
                        />
                    </div>
                ) : (
                    <div className="space-y-4 md:space-y-6">
                        <div className="flex justify-between items-end px-1 md:px-2">
                            <span className="w-1/2 text-left pr-2 font-bold text-slate-700 text-sm md:text-lg leading-tight">{leftLabel}</span>
                            <span className="w-1/2 text-right pl-2 font-bold text-slate-700 text-sm md:text-lg leading-tight">{rightLabel}</span>
                        </div>
                        
                        <div className="flex justify-center items-center gap-1 md:gap-2 py-2 md:py-4">
                            <ScaleTile value={-2} currentValue={value} onChange={onChange} dotCount={3} />
                            <ScaleTile value={-1} currentValue={value} onChange={onChange} dotCount={2} />
                            <ScaleTile value={0} currentValue={value} onChange={onChange} dotCount={1} />
                            <ScaleTile value={1} currentValue={value} onChange={onChange} dotCount={2} />
                            <ScaleTile value={2} currentValue={value} onChange={onChange} dotCount={3} />
                        </div>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {showHelp && (
                    <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="bg-blue-50/50 rounded-xl p-3 md:p-4 border border-blue-100 mt-2 md:mt-4">
                            <div className="flex gap-2 md:gap-3">
                                <div className="mt-0.5">
                                    <SafeIcon icon={FiAlertCircle} className="text-[#0270a9]" />
                                </div>
                                <p className="text-sm md:text-base text-slate-700 leading-relaxed whitespace-pre-line">
                                    {helpText}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    </div>
  );
}

function SelectionButton({ selected, onClick, label }) {
    return (
        <button
            onClick={onClick}
            className={clsx(
                "p-3 md:p-4 rounded-xl border-2 transition-all font-bold text-sm md:text-xl h-full flex items-center justify-center text-center",
                selected 
                    ? "bg-[#0270a9] border-[#0270a9] text-white shadow-lg scale-[1.02]" 
                    : "bg-white border-slate-200 text-slate-600 hover:border-[#0270a9] hover:text-[#0270a9] hover:bg-slate-50"
            )}
        >
            {label}
        </button>
    );
}

function ScaleTile({ value, currentValue, onChange, dotCount }) {
    const isSelected = currentValue === value;
    
    return (
        <button
            onClick={() => onChange(value)}
            className={clsx(
                "relative flex items-center justify-center transition-all duration-200 focus:outline-none rounded-md",
                "w-10 h-8 md:w-16 md:h-12",
                isSelected 
                    ? "bg-[#0270a9] shadow-lg scale-110 z-10 ring-2 ring-offset-2 ring-[#0270a9]" 
                    : "bg-[#0270a9]/40 hover:bg-[#0270a9]/60"
            )}
            title={`Select option`}
        >
            <div className="flex gap-1">
                {Array.from({ length: dotCount }).map((_, i) => (
                    <div key={i} className="w-1 h-1 md:w-2 md:h-2 bg-white rounded-full" />
                ))}
            </div>
        </button>
    );
}